import { PageContainer } from "@/components/core/page-container";
import { ResponsiveImage } from "@/components/ui/responsive-image";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { getOptimizedImageAsset } from "@/lib/image-assets";
import type { FeatureSection } from "@/types/site";

interface BespokeServicesSectionProps {
  content: FeatureSection;
}

export function BespokeServicesSection({
  content,
}: BespokeServicesSectionProps): JSX.Element {
  return (
    <section className="bg-white py-20 sm:py-24 md:py-40">
      <PageContainer className="flex flex-col items-center">
        <ScrollReveal className="mb-14 flex flex-col items-center space-y-5 sm:mb-16 sm:space-y-6 md:mb-24">
          {content.eyebrow ? (
            <p className="eyebrow text-center">{content.eyebrow}</p>
          ) : null}
          <h2 className="text-center font-display text-[2.15rem] tracking-[-0.01em] text-foreground sm:text-[2.5rem] md:text-[3rem]">
            {content.title}
          </h2>
          {content.description ? (
            <p className="max-w-2xl text-center text-base font-light text-muted-foreground sm:text-lg">
              {content.description}
            </p>
          ) : null}
          <div className="h-px w-12 bg-accent" aria-hidden="true" />
        </ScrollReveal>

        <div className="grid w-full gap-10 sm:gap-12 md:grid-cols-3 md:gap-8">
          {content.items.map((item, index) => (
            <ScrollReveal
              className={`group flex flex-col items-center ${
                index === 1 ? "md:mt-16" : index === 2 ? "md:mt-32" : ""
              }`}
              delay={index * 120}
              key={item.title}
            >
              {item.image && (
                <div className="relative mb-6 aspect-[3/4] w-full overflow-hidden bg-surface-muted sm:mb-8">
                  <ResponsiveImage
                    alt={item.title}
                    containerClassName="absolute inset-0"
                    height={getOptimizedImageAsset(item.image)?.height}
                    imgClassName="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out motion-safe:group-hover:scale-105 motion-safe:group-hover:rotate-[0.8deg]"
                    sizes="(min-width: 768px) 30vw, 100vw"
                    sources={getOptimizedImageAsset(item.image)?.sources}
                    src={item.image}
                    width={getOptimizedImageAsset(item.image)?.width}
                  />
                </div>
              )}
              <h3 className="mb-3 text-center font-display text-[1.8rem] text-foreground sm:mb-4 sm:text-[2rem]">
                {item.title}
              </h3>
              <p className="px-2 text-center text-base font-light leading-[1.6] text-muted-foreground sm:px-4">
                {item.description}
              </p>
            </ScrollReveal>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}
